import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import { FileText, History, Users, OctagonAlert, ArrowRight } from 'lucide-react';
import { VehicleStatus } from '../../../../shared/types/vehicle.types';
import useUser from '../../wrappers/use-user';
import useVehicle from '../../wrappers/use-vehicle';
import useVehicleTransfer from '../../wrappers/use-vehicle-transfer';
import { useTranslation } from 'react-i18next';
import {
    dashboardStyles,
    getDashboardCardIconStyles,
} from './Dashboard.styles';

interface DashboardStats {
    pendingRegistrations: number;
    pendingTransfers: number;
    totalUsers: number;
}

const Dashboard: React.FC = () => {
    const { t } = useTranslation();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);
    const { list: listUsers } = useUser();
    const { list: listVehicles } = useVehicle();
    const { list: listTransfers } = useVehicleTransfer();

    useEffect(() => {
        const fetchStats = async () => {
            const [pendingVehicles, pendingTransfers, users] = await Promise.all([
                listVehicles({ status: [VehicleStatus.Pending, VehicleStatus.InfoRequested] }),
                listTransfers({ status: ['pending', 'infoRequested'] }),
                listUsers({ role: 'customer' }),
            ]);
            setStats({
                pendingRegistrations: pendingVehicles.length,
                pendingTransfers: pendingTransfers.length,
                totalUsers: users.total,
            });
            setLoading(false);
        };
        fetchStats();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const statItems = [
        { name: t('adminDashboard.pendingRegistrations'), stat: stats?.pendingRegistrations ?? '—', icon: FileText, href: '/app/admin/vehicle-registrations', color: '#f59e0b' },
        { name: t('adminDashboard.pendingTransfers'), stat: stats?.pendingTransfers ?? '—', icon: History, href: '/app/admin/transfers', color: '#3b82f6' },
        { name: t('adminDashboard.activeUsers'), stat: stats?.totalUsers ?? '—', icon: Users, href: '/app/admin/users', color: '#22c55e' },
        { name: t('adminDashboard.newIncidents'), stat: 0, icon: OctagonAlert, href: '/app/admin/audit-log', color: '#ef4444' },
    ];

    return (
        <Box sx={dashboardStyles.root}>
            <Typography variant="h4" fontWeight={700}>{t('adminDashboard.title')}</Typography>

            <Box>
                <Typography variant="h5" fontWeight={700} mb={3}>{t('adminDashboard.systemOverview')}</Typography>
                {loading ? (
                    <Box sx={dashboardStyles.loadingContainer}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {statItems.map((item) => {
                            const Icon = item.icon;
                            return (
                                <Grid item xs={12} sm={6} lg={3} key={item.name}>
                                    <Paper
                                        elevation={0}
                                        sx={dashboardStyles.statCard}
                                    >
                                        <Box sx={dashboardStyles.statCardContent}>
                                            <Box sx={getDashboardCardIconStyles(item.color)}>
                                                <Icon size={24} color="white" />
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">{item.name}</Typography>
                                                <Typography variant="h5" fontWeight={700}>{item.stat}</Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={dashboardStyles.statCardFooter}>
                                            <Button
                                                component={Link}
                                                to={item.href}
                                                endIcon={<ArrowRight size={20} />}
                                                size="small"
                                                sx={dashboardStyles.detailButton}
                                            >
                                                {t('adminDashboard.viewDetail')}
                                            </Button>
                                        </Box>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Box>
        </Box>
    );
};

export default Dashboard;
